import { useCallback, useEffect, useRef } from "react";

import { UserLogout } from "../redux/user";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

const events = [
  "load",
  "mousemove",
  "mousedown",
  "click",
  "scroll",
  "keypress",
];

export const AutoLogout = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const timer = useRef(null);
  const INACTIVITY_LIMIT = 300000; // 5 minutes in milliseconds

  // A memoized function to clear the timer
  const clearTimer = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
  }, []);

  // A memoized function that handles the logout logic
  const handleLogout = useCallback(() => {
    clearTimer();
    dispatch(UserLogout()); // 👈 Dispatch the central logout action
    navigate("/login"); // 👈 Redirect after logout
  }, [clearTimer, dispatch, navigate]);

  // A memoized function to reset the timer
  const resetTimer = useCallback(() => {
    clearTimer();
    timer.current = setTimeout(handleLogout, INACTIVITY_LIMIT);
  }, [clearTimer, handleLogout, INACTIVITY_LIMIT]);

  // Add and remove event listeners on mount and unmount
  useEffect(() => {
    // Set an initial timer when the component mounts
    resetTimer();

    // Add event listeners to reset the timer on user activity
    events.forEach((event) => window.addEventListener(event, resetTimer));

    // Cleanup function: this runs when the component unmounts
    return () => {
      // Clear the timer
      clearTimer();
      // Remove all event listeners to prevent memory leaks
      events.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [clearTimer, resetTimer]); // Dependencies are the stable functions

  return children;
};
